/**
 * Contextual help texts for admin pages and form fields.
 * Used by <PageHelp> and <FieldHelp> components.
 */
// ── Page-level help ──────────────────────────────────────────────────
export const pageHelp = {
	// Content
	entities: {
		title: "Entities",
		description:
			"Entities are brands, casinos, game providers, or any reviewable item. Each entity has a type, niche data, and properties.",
		tips: [
			"Set status to 'published' to make it visible on the site",
			"Use bulk actions (checkboxes) to publish/archive multiple at once",
			"Entities need a review + offer to appear fully on the site",
		],
	},
	entityDetail: {
		title: "Entity Editor",
		description:
			"Create or edit an entity. Fill in basic info, niche data (determines URL category), and properties (displayed as facts table).",
		tips: [
			"Slug is auto-generated from name — edit it only if needed",
			'Niche Data determines the URL: {"niche":"casino"} → /casino/slug',
			"Properties appear as a facts table on the entity page",
		],
	},
	reviews: {
		title: "Reviews",
		description:
			"Expert reviews linked to entities. Each review has category scores, pros/cons, and a verdict.",
		tips: [
			"One review per entity per locale",
			"Overall score is shown on entity cards across the site",
			"Use AI Draft to generate a starting point, then edit",
		],
	},
	reviewDetail: {
		title: "Review Editor",
		description:
			"Write or edit an expert review. Set category scores (0-10), add pros/cons, and write a verdict.",
		tips: [
			"Category scores auto-calculate the overall rating",
			"Pros/cons should be specific and actionable",
			"Verdict is shown prominently — keep it 2-3 sentences",
		],
	},
	offers: {
		title: "Offers & Bonuses",
		description:
			"Promotions and bonuses linked to entities. Each offer has a type, value, and terms.",
		tips: [
			"Higher Sort Order = shown first in lists",
			"Mark exclusive offers with the Featured toggle",
			"Set Valid Until to auto-expire offers",
		],
	},
	offerDetail: {
		title: "Offer Editor",
		description:
			"Create or edit a bonus/promotion. Link it to an entity and set terms & conditions.",
	},
	authors: {
		title: "Authors",
		description:
			"Authors are linked to reviews and articles. Each has a bio, expertise, and social links.",
	},
	authorDetail: {
		title: "Author Editor",
		description:
			"Create or edit an author profile. Authors appear on review pages with their byline.",
	},
	comparisons: {
		title: "Comparisons",
		description:
			"Side-by-side entity comparisons. Auto-generated from entity pairs or created manually.",
	},
	answers: {
		title: "Answers",
		description: "Q&A content linked to entities. Appears in knowledge panels and FAQ sections.",
	},
	faq: {
		title: "FAQ Management",
		description:
			"Frequently asked questions. Can be global or linked to specific entities for contextual display.",
	},
	pages: {
		title: "Pages",
		description:
			"Static content pages (about, methodology, privacy, etc.). Each has a template and locale.",
	},
	pageDetail: {
		title: "Page Editor",
		description:
			"Create or edit a static page. Use templates for consistent layout, add SEO meta tags.",
	},
	media: {
		title: "Media Library",
		description:
			"Upload and manage images, logos, and screenshots. Files are stored in Cloudflare R2.",
		tips: [
			"Supported: JPG, PNG, WebP, SVG",
			"Copy the URL to use in entity properties or reviews",
			"Images are served via Cloudflare CDN",
		],
	},
	taxonomies: {
		title: "Taxonomy",
		description: "Tags, categories, and hierarchical classifications for organizing content.",
	},
	// News
	news: {
		title: "News Articles",
		description:
			"News articles with topics and geo-tagging. Can be linked to entities for contextual news.",
	},
	newsArticleDetail: {
		title: "Article Editor",
		description:
			"Write or edit a news article. Assign topics, set locale, and link to relevant entities.",
	},
	rssSources: {
		title: "RSS Sources",
		description:
			"External RSS feeds for news aggregation. Articles are auto-imported and moderated.",
	},
	liveBlogs: {
		title: "Live Blogs",
		description: "Real-time event coverage with timestamped updates.",
	},
	topics: {
		title: "Topics",
		description: "News categories and tags. Used for topic pages and article classification.",
	},
	// Quality
	moderation: {
		title: "Moderation",
		description: "Review and approve/reject user-submitted reviews and comments.",
		tips: [
			"Items are sorted by submission date (newest first)",
			"AI moderation flags suspicious content automatically",
			"Approved reviews appear on entity pages",
		],
	},
	freshness: {
		title: "Freshness",
		description: "Track content freshness. Entities with stale content are flagged for review.",
		tips: [
			"Green = updated recently, Yellow = needs attention, Red = stale",
			"Click an entity to update its freshness status",
		],
	},
	ranking: {
		title: "Ranking",
		description:
			"Configure ranking weights that determine entity order in Top lists and search results.",
		tips: [
			"Higher weight = more influence on final position",
			"Pin position overrides all ranking calculations",
			"Changes apply to all Top pages on the site",
		],
	},
	externalSignals: {
		title: "External Signals",
		description:
			"Trust signals from external sources — license validity, complaint ratios, industry ratings.",
	},
	performance: {
		title: "Performance",
		description: "Page views, engagement metrics, and content performance analytics.",
	},
	programmatic: {
		title: "Programmatic Pages",
		description:
			"Auto-generated pages from templates and data patterns (e.g., 'Best casinos in {city}').",
	},
	abTests: {
		title: "A/B Tests",
		description: "Create and manage split tests for content and layout variations.",
	},
	auditLog: {
		title: "Audit Log",
		description:
			"All staff actions logged automatically. Filter by user, action type, or date range.",
	},
	// Platform
	globalEntities: {
		title: "Global Entities",
		description:
			"Central entity registry shared across all sites. Changes here propagate to site-specific versions.",
	},
	intelligence: {
		title: "Intelligence",
		description: "AI-powered insights: content gaps, competitor analysis, trend detection.",
	},
	sites: {
		title: "Sites",
		description: "All registered sites in the platform. View configuration, domains, and status.",
	},
	staff: {
		title: "Staff",
		description: "Manage admin users and their roles (admin, editor, viewer).",
	},
	platformUsers: {
		title: "Platform Users",
		description:
			"Cross-site visitor accounts. Users can have reviews, preferences, and activity across sites.",
	},
	cioWorkspaces: {
		title: "CIO Workspaces",
		description:
			"Customer.io integration settings. Each workspace handles email campaigns for a group of sites.",
	},
	aiModels: {
		title: "AI Models",
		description: "View connected AI providers, test connections, and monitor token usage.",
	},
	partners: {
		title: "Partners",
		description: "Partner management — networks, deals, commissions, and tracking.",
	},
	import: {
		title: "Import",
		description: "Import entities, reviews, or offers from CSV/JSON files.",
	},
	export: {
		title: "Export",
		description: "Export content to CSV or JSON for backup or analysis.",
	},
	trash: {
		title: "Trash",
		description: "Deleted items. Restore or permanently remove within 30 days.",
	},
	settings: {
		title: "Settings",
		description: "Site-level configuration: theme, SEO defaults, feature flags.",
	},
	dashboard: {
		title: "Dashboard",
		description: "Overview of content stats, recent activity, and quick actions.",
	},
};
// ── Field-level help ─────────────────────────────────────────────────
export const fieldHelp = {
	entity: {
		name: "Display name of the entity (brand, casino, etc.)",
		slug: "URL-friendly identifier. Auto-generated from name. Used in the URL: /casino/{slug}",
		type: "Entity category. Most casino brands use 'brand'. Determines available features.",
		locale: "Content language code: en, de, es, sv. Each entity exists per-locale.",
		website: "Official website URL. Used for external links and verification.",
		description: "Rich text description shown on the entity page. Supports formatting.",
		properties:
			"Key-value pairs displayed as a facts table (e.g., Founded: 2006, Min Deposit: $10)",
		nicheData:
			'JSON that determines routing and category. Must include {"niche": "casino"} for casino brands. Other fields: licenses, paymentMethods, gameProviders, currencies.',
		status: "draft = hidden, published = live on site, archived = hidden but preserved",
	},
	review: {
		entity: "The entity being reviewed. One review per entity per locale.",
		title: "Review headline. Appears in search results and cards.",
		slug: "URL path: /reviews/{slug}. Auto-generated from title.",
		content:
			"Full review body. Use headings for sections: Overview, Bonuses, Games, Payments, Support.",
		prosCons:
			"Structured advantages/disadvantages. Be specific: 'Fast payouts under 24h' not 'Good payouts'.",
		overallRating: "Calculated from category scores. Manually adjustable. Range: 1.0 - 10.0.",
		verdict: "2-3 sentence conclusion. Shown prominently on the review page.",
		status: "draft = not visible, published = live, archived = hidden",
		locale: "Language of this review. Must match the entity's locale.",
	},
	offer: {
		entity: "The entity (casino) this offer belongs to.",
		offerName: "Bonus title shown to users (e.g., '100% up to $500 + 200 Free Spins')",
		type: "welcome_bonus, no_deposit, free_spins, cashback, reload, promo_code",
		description: "Detailed bonus description and how to claim it.",
		amount: "Bonus value (e.g., '500', '200 spins', '50%')",
		currency: "Currency of the bonus value (USD, EUR, GBP, etc.)",
		conditions: "Terms as key-value pairs: wagering, minDeposit, maxBet, validDays",
		promoCode: "Bonus code users need to enter. Leave empty if no code required.",
		validFrom: "Start date. Leave empty for immediately active offers.",
		validUntil: "Expiry date. Expired offers are auto-hidden from the site.",
		affiliateLinkId: "ID of the affiliate tracking link for this offer.",
		featured: "Featured offers get a badge and appear first in lists.",
		sortOrder: "Higher number = shown first. Use for manual ordering.",
	},
	author: {
		name: "Author display name shown on reviews and articles.",
		slug: "URL path for the author profile page.",
		role: "Author role: writer, editor, expert, correspondent.",
		bio: "Short biography shown on review pages. 2-3 sentences.",
		photoUrl: "Avatar image URL. Upload to Media Library first, then paste URL here.",
		expertiseAreas: "Comma-separated expertise tags (e.g., 'casino reviews, gambling industry').",
		socialLinks: "Social media profile URLs (Twitter, LinkedIn, etc.).",
		isCorrespondent: "Correspondents are shown in the news team section.",
	},
	page: {
		title: "Page title shown in browser tab and as the main heading.",
		slug: "URL path: /{locale}/{slug} (e.g., /en/about, /de/privacy)",
		template: "Layout template: default, legal, landing. Determines page structure.",
		locale: "Page language. Each page exists independently per locale.",
		status: "draft = not accessible, published = live",
		seoTitle: "Custom title for search engines. Leave empty to use the page title.",
		seoDescription: "Meta description for search results. 150-160 characters recommended.",
		content: "Page body. Rich text with headings, lists, links, and images.",
	},
	newsArticle: {
		title: "Article headline. Keep under 70 characters for optimal display.",
		slug: "URL: /news/{slug}. Auto-generated from title.",
		content: "Article body. Use headings to structure the content.",
		contentType: "article = standard, breaking = urgent, analysis = in-depth, opinion = editorial",
		author: "Select the article author from the authors list.",
		status: "draft = not visible, published = live, archived = hidden",
		locale: "Article language. Must match available site locales.",
		topics: "Tags for categorization. Used for topic pages and filtering.",
	},
};
//# sourceMappingURL=help-texts.js.map
